/**
 * Browser-side persistence for the player's chosen UI locale.
 *
 * The shared {@link setLocale} helper only updates the in-memory i18n
 * registry, so a page reload would otherwise drop back to the default
 * `en`. Mirroring it through `localStorage` keeps the language sticky
 * across sessions, the same way `vs_input_settings_v1` keeps the
 * invert-Y toggles sticky.
 *
 * `loadPersistedLocale()` is called once from the bootstrap (before any
 * UI is built) so the auth screen, main menu, and pause menu all read
 * the same already-applied value when they first mount.
 */

import { setLocale, getAvailableLocales, type Locale } from '@viral-space/shared';

const STORAGE_KEY = 'vs_locale_v1';

/**
 * Read the persisted locale (if any) and apply it to the i18n
 * registry. Returns the applied locale, or `null` when nothing was
 * stored / the stored value is no longer a registered locale.
 *
 * Locales that have been removed since the last session are
 * intentionally ignored rather than silently picked: it's better to
 * fall back to the default `en` than to surface broken UI keys.
 */
export function loadPersistedLocale(): Locale | null {
  if (typeof localStorage === 'undefined') return null;
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  if (!getAvailableLocales().includes(raw)) return null;
  setLocale(raw);
  return raw;
}

/**
 * Persist the current locale so the next page load picks it up
 * automatically. Silently no-ops when `localStorage` is unavailable
 * (private mode, very old WebViews) — the i18n registry has already
 * been updated in memory by the caller.
 */
export function persistLocale(locale: Locale): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // ignore quota / disabled-storage errors
  }
}
